import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useGetEventJoinRequestsQuery,
  useLazyGetEventJoinRequestsQuery,
  useDeleteEventJoinRequestMutation,
  useResendEventTicketMutation,
} from '@/store/api/eventsApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader } from '@/components/common/Loader';
import { ErrorBanner } from '@/components/common/ErrorBanner';
import { Modal } from '@/components/common/Modal';
import { useNotifications } from '@/hooks/useNotifications';
import { Input } from '@/components/ui/input';
import { useDebounce } from '@/hooks/useDebounce';
import {
  Users,
  Mail,
  Phone,
  Linkedin,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Search,
  Trash2,
  Loader2,
  Download,
  Send,
  CheckCircle,
  XCircle,
} from 'lucide-react';

export function EventJoinRequestsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlEventId = searchParams.get('eventId') || '';

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(10);
  const [timeFilter, setTimeFilter] = useState<'Upcoming' | 'Past' | ''>('');
  const skip = (page - 1) * take;

  const [deleteRequest, { isLoading: isDeleting }] = useDeleteEventJoinRequestMutation();
  const [resendTicket, { isLoading: isResending }] = useResendEventTicketMutation();
  const [requestToDelete, setRequestToDelete] = useState<{ id: number; name: string } | null>(null);
  const [requestToResend, setRequestToResend] = useState<number | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const { success: notifySuccess, error: notifyError } = useNotifications();

  const {
    data: response,
    isLoading,
    error,
    isFetching,
  } = useGetEventJoinRequestsQuery({
    skip,
    take,
    search: debouncedSearch,
    eventId: urlEventId,
    timeFilter,
  });

  const [fetchForExport, { isFetching: isExporting }] = useLazyGetEventJoinRequestsQuery();

  React.useEffect(() => {
    setPage(1);
  }, [debouncedSearch, take, urlEventId, timeFilter]);

  const confirmDelete = async () => {
    if (!requestToDelete || !deleteReason.trim()) return;
    try {
      await deleteRequest({ id: requestToDelete.id, reason: deleteReason.trim() }).unwrap();
      notifySuccess('Join request deleted and cancellation email sent successfully.');
      setRequestToDelete(null);
      setDeleteReason('');
    } catch (err: any) {
      notifyError(err?.data?.message || 'Failed to delete join request. Please try again.');
    }
  };

  const handleResendTicket = async (eventId: number, requestId: number) => {
    try {
      setRequestToResend(requestId);
      await resendTicket({ eventId, requestId }).unwrap();
      notifySuccess('Ticket email resent successfully.');
    } catch (err: any) {
      notifyError(err?.data?.message || 'Failed to resend ticket email.');
    } finally {
      setRequestToResend(null);
    }
  };

  const handleExportCSV = async () => {
    try {
      // Fetch all requests matching current filters
      const result = await fetchForExport({
        skip: 0,
        take: 1000,
        search: debouncedSearch,
        eventId: urlEventId,
        timeFilter,
      }).unwrap();

      if (!result.data || result.data.length === 0) {
        notifyError('No data available to export.');
        return;
      }

      // Build CSV
      const headers = ['Name', 'Email', 'Phone', 'Event', 'LinkedIn', 'Date'];
      const csvRows = [headers.join(',')];

      result.data.forEach((req) => {
        const row = [
          `"${req.fullName.replace(/"/g, '""')}"`,
          `"${req.email.replace(/"/g, '""')}"`,
          `"${(req.phoneNumber || '').replace(/"/g, '""')}"`,
          `"${(req.event?.title || `Event #${req.eventId}`).replace(/"/g, '""')}"`,
          `"${(req.linkedin || '').replace(/"/g, '""')}"`,
          `"${new Date(req.createdAt).toLocaleDateString()}"`,
        ];
        csvRows.push(row.join(','));
      });

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `join_requests_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      notifyError(err?.data?.message || 'Failed to export CSV. Please try again.');
    }
  };

  const totalPages = response ? Math.ceil(response.count / take) || 1 : 1;
  const requests = response?.data || [];

  return (
    <div className="space-y-6">
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Event Join Requests</h1>
            <p className="text-muted-foreground mt-1">
              List of all users who have submitted a request to join an event.
            </p>
          </div>
          {requests.length > 0 && (
            <Button onClick={handleExportCSV} disabled={isExporting} className="w-full sm:w-auto">
              {isExporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export CSV
            </Button>
          )}
        </div>
        {urlEventId && (
          <div className="mt-4 flex items-center gap-2">
            <Badge variant="secondary" className="px-3 py-1.5 text-sm">
              Filtering for Event #{urlEventId}
            </Badge>
            <Button variant="ghost" size="sm" onClick={() => setSearchParams({})}>
              Clear Filter
            </Button>
          </div>
        )}
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Join Requests
            </CardTitle>
            <CardDescription>
              {response
                ? `${response.count} total request${response.count !== 1 ? 's' : ''}`
                : 'Loading...'}
            </CardDescription>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap items-center gap-2 mt-4 sm:mt-0 w-full sm:w-auto">
            {/* Time Filter - only show if not filtering by a specific event ID */}
            {!urlEventId && (
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value as any)}
                className="h-9 w-full sm:w-auto rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="">All Events</option>
                <option value="Upcoming">Upcoming Events</option>
                <option value="Past">Past Events</option>
              </select>
            )}
            {/* Items per page filter */}
            <select
              value={take}
              onChange={(e) => setTake(Number(e.target.value))}
              className="h-9 w-full sm:w-auto rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            {/* Search */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search name, email..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {isFetching && (
              <div className="absolute inset-0 bg-background/50 z-10 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}

            {isLoading && !requests.length ? (
              <Loader />
            ) : error ? (
              <ErrorBanner message="Failed to load join requests." />
            ) : requests.length > 0 ? (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-muted-foreground uppercase bg-muted/50 border-b">
                    <tr>
                      <th className="px-4 py-3 font-medium">Name</th>
                      <th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Phone</th>
                      <th className="px-4 py-3 font-medium">Event</th>
                      <th className="px-4 py-3 font-medium">LinkedIn</th>
                      <th className="px-4 py-3 font-medium">Date</th>
                      <th className="px-4 py-3 font-medium">Ticket Status</th>
                      <th className="px-4 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {requests.map((req) => (
                      <tr key={req.id} className="hover:bg-muted/50 transition-colors">
                        <td className="px-4 py-4 font-medium text-foreground">{req.fullName}</td>
                        <td className="px-4 py-4">{req.email}</td>
                        <td className="px-4 py-4">
                          {req.phoneNumber ? (
                            <span className="flex items-center gap-1.5">{req.phoneNumber}</span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <Badge variant="secondary" className="font-normal">
                            {req.event?.title || `Event #${req.eventId}`}
                          </Badge>
                        </td>
                        <td className="px-4 py-4">
                          {req.linkedin ? (
                            <a
                              href={req.linkedin}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800"
                            >
                              <Linkedin className="h-3.5 w-3.5" />
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-muted-foreground">
                          {new Date(req.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-4">
                          {req.ticketSent ? (
                            <Badge variant="outline" className="text-green-600 bg-green-50 border-green-200 gap-1">
                              <CheckCircle className="h-3 w-3" /> Sent
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-red-600 bg-red-50 border-red-200 gap-1">
                              <XCircle className="h-3 w-3" /> Failed
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-blue-600 hover:text-blue-800"
                              onClick={() => handleResendTicket(req.eventId, req.id)}
                              disabled={isResending && requestToResend === req.id}
                              title="Resend Ticket Email"
                            >
                              {isResending && requestToResend === req.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Send className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() =>
                                setRequestToDelete({
                                  id: req.id,
                                  name: req.fullName,
                                })
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground border rounded-md">
                <div className="flex justify-center mb-4">
                  <Users className="h-10 w-10 text-muted-foreground/30" />
                </div>
                <p className="font-medium mb-2">No join requests found.</p>
                <p className="text-sm">Try adjusting your search or filter.</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {response && response.count > 0 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Showing <span className="font-medium">{skip + 1}</span> to{' '}
                <span className="font-medium">{Math.min(skip + take, response.count)}</span> of{' '}
                <span className="font-medium">{response.count}</span> results
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>
                <div className="text-sm font-medium">
                  Page {page} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={!!requestToDelete}
        onClose={() => {
          if (!isDeleting) {
            setRequestToDelete(null);
            setDeleteReason('');
          }
        }}
        title="Delete Join Request"
      >
        <div className="space-y-4 pt-2 text-sm">
          <p className="text-muted-foreground">
            Are you sure you want to delete the join request from{' '}
            <span className="font-semibold text-foreground">{requestToDelete?.name}</span>? This
            action cannot be undone.
          </p>
          <div className="space-y-2">
            <label className="font-medium">
              Reason for Cancellation <span className="text-destructive">*</span>
            </label>
            <p className="text-xs text-muted-foreground">
              This reason will be included in the cancellation email sent to the user.
            </p>
            <textarea
              className="w-full min-h-[100px] p-3 rounded-md border border-input bg-background shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="e.g. Your registration was cancelled because..."
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              disabled={isDeleting}
            />
          </div>
        </div>
        <div className="mt-6 flex flex-col sm:flex-row-reverse gap-3 border-t pt-4">
          <Button
            variant="destructive"
            onClick={confirmDelete}
            disabled={isDeleting || !deleteReason.trim()}
            className="w-full sm:w-auto"
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting...
              </>
            ) : (
              'Confirm Delete & Send Email'
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setRequestToDelete(null);
              setDeleteReason('');
            }}
            disabled={isDeleting}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
        </div>
      </Modal>
    </div>
  );
}
